import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';

export type ColorThemeType = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ColorService {
  private readonly themeState: WritableSignal<ColorThemeType> = signal<ColorThemeType>('light');
  readonly theme: Signal<ColorThemeType> = this.themeState.asReadonly();
  readonly isDarkTheme: Signal<boolean> = computed(() => this.theme() === 'dark');

  readonly textColor: Signal<string> = this.cssVariable('--text-color');
  readonly backgroundColor: Signal<string> = this.cssVariable('--background-color');

  readonly neutral100: Signal<string> = this.cssVariable('--neutral-100');
  readonly neutral200: Signal<string> = this.cssVariable('--neutral-200');
  readonly neutral300: Signal<string> = this.cssVariable('--neutral-300');
  readonly neutral400: Signal<string> = this.cssVariable('--neutral-400');
  readonly neutral500: Signal<string> = this.cssVariable('--neutral-500');
  readonly neutral600: Signal<string> = this.cssVariable('--neutral-600');
  readonly neutral700: Signal<string> = this.cssVariable('--neutral-700');
  readonly neutral900: Signal<string> = this.cssVariable('--neutral-900');

  readonly brandPrimary: Signal<string> = this.cssVariable('--brand-primary-600');
  readonly brandSecondary: Signal<string> = this.cssVariable('--brand-secondary-600');
  readonly brandSecondary100: Signal<string> = this.cssVariable('--brand-secondary-100');
  readonly brandSecondary200: Signal<string> = this.cssVariable('--brand-secondary-200');
  readonly brandSecondary400: Signal<string> = this.cssVariable('--brand-secondary-400');
  readonly brandSecondary500: Signal<string> = this.cssVariable('--brand-secondary-500');
  readonly brandSecondary600: Signal<string> = this.cssVariable('--brand-secondary-600');


  readonly success: Signal<string> = this.cssVariable('--info-success-500');
  readonly success100: Signal<string> = this.cssVariable('--info-success-100');
  readonly success200: Signal<string> = this.cssVariable('--info-success-200');
  readonly success700: Signal<string> = this.cssVariable('--info-success-700');

  readonly warn: Signal<string> = this.cssVariable('--warning-500');
  readonly danger500: Signal<string> = this.cssVariable('--danger-500');
  readonly danger100: Signal<string> = this.cssVariable('--danger-100');

  private readonly mutationObserver: MutationObserver;

  constructor() {
    this.initColorTheme();

    this.mutationObserver = new MutationObserver(() => {
      this.themeState.set(this.readThemeFromBody());
    });
    this.mutationObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  public getCssVariable(name: string): string {
    return getComputedStyle(document.body).getPropertyValue(name).trim();
  }

  get currentTheme(): ColorThemeType {
    return this.theme();
  }

  setTheme(theme: ColorThemeType): void {
    document.body.classList.remove('light', 'dark');
    document.body.classList.add(theme);
    localStorage.setItem('color-theme', theme);
    this.themeState.set(theme);
  }

  toggleColorTheme(isLight: boolean): void {
    this.setTheme(isLight ? 'light' : 'dark');
  }

  initColorTheme(): void {
    const localColorThemePreference: string | null = localStorage.getItem('color-theme');
    const theme: ColorThemeType = localColorThemePreference
      ? this.toColorTheme(localColorThemePreference)
      : this.getPreferredColorTheme();

    this.setTheme(theme);
  }

  private cssVariable(name: string): Signal<string> {
    return computed(() => {
      this.theme();
      return this.getCssVariable(name);
    });
  }

  private readThemeFromBody(): ColorThemeType {
    return document.body.classList.contains('dark') ? 'dark' : 'light';
  }

  private getPreferredColorTheme(): ColorThemeType {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private toColorTheme(theme: string): ColorThemeType {
    return theme === 'dark' ? 'dark' : 'light';
  }
}
